// src/WebsiteBuilderView.jsx
// ═══════════════════════════════════════════════════════════════════════════
// Website Builder workbench. Two-pane layout:
//   LEFT  — conversation with Aria (text chat)
//   RIGHT — live preview iframe that shows the website as it's built
//
// The user describes what they want; Aria returns a fresh HTML document.
// Each response replaces the iframe srcDoc, so the preview updates in real
// time as the conversation goes.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect } from 'react';
import './WebsiteBuilderView.css';
import { getApiBase, fetchWithTimeout } from './apiBase';
import { sfx } from './ariaSoundFx';

const API_BASE = getApiBase();

// Starter HTML the iframe shows before Aria has built anything. Keeps the
// preview from looking broken on first load.
const STARTER_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Your website</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif;
      background: linear-gradient(135deg, #0c0c10 0%, #1a1a24 100%);
      color: rgba(255,255,255,0.85);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      text-align: center;
    }
    .placeholder { max-width: 520px; }
    h1 { font-size: 2.2rem; margin-bottom: 14px; font-weight: 700; letter-spacing: -0.02em; }
    p { font-size: 1rem; line-height: 1.55; color: rgba(255,255,255,0.55); }
    .hint {
      margin-top: 24px;
      padding: 14px 18px;
      border-radius: 14px;
      background: rgba(252, 211, 77, 0.08);
      border: 1px solid rgba(252, 211, 77, 0.18);
      color: #FCD34D;
      font-size: 0.92rem;
    }
  </style>
</head>
<body>
  <div class="placeholder">
    <h1>Your website lives here</h1>
    <p>Tell Aria what you want on the left — landing page, portfolio, restaurant menu, blog — and watch it appear in this window in real time.</p>
    <div class="hint">Try: "Build me a landing page for a coffee shop called Ember"</div>
  </div>
</body>
</html>`;

/**
 * Pull the first <!DOCTYPE html> ... </html> block out of an LLM reply.
 * Aria sometimes wraps the HTML in markdown fences or adds chat preamble;
 * this extracts the doc cleanly. Returns null if no full doc is found.
 */
function extractHtmlDocument(text) {
  if (!text || typeof text !== 'string') return null;
  // Strip code fences if present.
  const fenceMatch = text.match(/```(?:html)?\s*([\s\S]*?)```/i);
  const candidate = fenceMatch ? fenceMatch[1] : text;
  // Look for the first complete HTML doc.
  const docMatch = candidate.match(/<!DOCTYPE html[\s\S]*?<\/html>/i);
  if (docMatch) return docMatch[0];
  // Fallback: a partial document starting from <html.
  const htmlMatch = candidate.match(/<html[\s\S]*?<\/html>/i);
  if (htmlMatch) return `<!DOCTYPE html>${htmlMatch[0]}`;
  return null;
}

/**
 * Strip any HTML from a reply so the chat panel only shows the conversational
 * part. (If the user asked for a coffee landing page, we don't want a wall
 * of HTML in the chat — only Aria's "Here's your site, want the header
 * bigger?" line.)
 */
function stripHtmlFromReply(text) {
  if (!text) return '';
  let out = text;
  // Remove fenced HTML blocks.
  out = out.replace(/```(?:html)?\s*[\s\S]*?```/gi, '').trim();
  // Remove raw full HTML docs.
  out = out.replace(/<!DOCTYPE html[\s\S]*?<\/html>/gi, '').trim();
  out = out.replace(/<html[\s\S]*?<\/html>/gi, '').trim();
  return out;
}

export default function WebsiteBuilderView() {
  // Top-of-workbench mode toggle: 'website' or 'app'. Same surface, two
  // build targets — Website builds a static HTML page in the iframe, App
  // would build an interactive single-page app (not wired yet).
  const [mode, setMode] = useState('website');
  const [messages, setMessages] = useState(() => [
    {
      role: 'aria',
      text: "Hi — tell me what kind of website you want and I'll build it on the right. Landing page, portfolio, menu, blog — anything.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [siteHtml, setSiteHtml] = useState(STARTER_HTML);
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' | 'mobile'
  const [leftTab, setLeftTab] = useState('chat'); // 'chat' | 'code'
  const [codeCopied, setCodeCopied] = useState(false);
  // Draggable split — user controls how wide left vs right is.
  // Per-mode ratios: desktop has its OWN remembered position, mobile has its
  // own. Defaults differ because the mobile phone-frame is narrow, so the
  // left panel naturally wants more room on mobile.
  //   desktop default: 0.40  (40% left, 60% right — preview gets most)
  //   mobile  default: 0.65  (65% left, 35% right — phone-frame is narrow)
  // Stored separately in localStorage so each survives reloads.
  const DEFAULT_RATIOS = { desktop: 0.40, mobile: 0.65 };
  const [splitRatios, setSplitRatios] = useState(() => {
    const load = (key, def) => {
      try {
        const v = parseFloat(localStorage.getItem(key) || '');
        return (isFinite(v) && v >= 0.15 && v <= 0.85) ? v : def;
      } catch (_) { return def; }
    };
    return {
      desktop: load('wb_split_ratio_desktop', DEFAULT_RATIOS.desktop),
      mobile:  load('wb_split_ratio_mobile',  DEFAULT_RATIOS.mobile),
    };
  });
  // Active ratio follows the currently-selected preview device.
  const splitRatio = splitRatios[previewDevice] ?? DEFAULT_RATIOS[previewDevice];
  const splitContainerRef = useRef(null);
  const draggingRef = useRef(false);
  // Mirror as state so we can toggle the transition: animate on mode-switch
  // / reset / double-click, but stay snappy during an active drag.
  const [isDragging, setIsDragging] = useState(false);
  const previewDeviceRef = useRef(previewDevice);
  useEffect(() => { previewDeviceRef.current = previewDevice; }, [previewDevice]);

  // Drag handlers — mouse + touch. Updates ONLY the current mode's ratio.
  useEffect(() => {
    function onMove(e) {
      if (!draggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const ratio = Math.max(0.15, Math.min(0.85, (clientX - rect.left) / rect.width));
      const mode = previewDeviceRef.current;
      setSplitRatios(r => ({ ...r, [mode]: ratio }));
      try { localStorage.setItem(`wb_split_ratio_${mode}`, String(ratio)); } catch (_) {}
      if (e.cancelable) e.preventDefault();
    }
    function onUp() {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
    };
  }, []);

  const onDividerDown = (e) => {
    draggingRef.current = true;
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    if (e.cancelable) e.preventDefault();
  };

  // Refresh button — resets BOTH ratios to defaults. Does NOT reload the
  // page or the iframe. Owner asked 2026-06-02: "thats not supposed to
  // refresh the page just the layout".
  const resetLayout = () => {
    sfx.click();
    setSplitRatios({ ...DEFAULT_RATIOS });
    try {
      localStorage.removeItem('wb_split_ratio_desktop');
      localStorage.removeItem('wb_split_ratio_mobile');
    } catch (_) {}
  };

  // When switching modes, snap the preview to the device size that matches.
  // Website → desktop (wide marketing canvas). App → mobile (phone canvas).
  useEffect(() => {
    setPreviewDevice(mode === 'app' ? 'mobile' : 'desktop');
  }, [mode]);
  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll chat to bottom on new message.
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    sfx.generate();
    setInput('');
    const userMessage = { role: 'user', text };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    // Build the prompt Aria will see. We give her the current HTML so she
    // edits it instead of starting over each turn. The brief changes based
    // on whether the user is building a marketing site or an interactive app.
    const systemPrompt = mode === 'app'
      ? [
          'You are Aria, an expert mobile app builder. The user is iterating on an',
          'interactive single-page mobile app. Each turn, return ONE complete, standalone',
          'HTML document (with inline <style> and inline <script> — vanilla JS only,',
          'no external assets, no network fetches). The document must start with',
          '<!DOCTYPE html> and end with </html>. Design for a 390x844 iPhone viewport:',
          'bottom tab bar, large touch targets, native-feeling transitions, real state',
          '(use JS for taps, navigation, form input). BEFORE the HTML, write one short',
          'conversational sentence (under 20 words) acknowledging the request.',
          'Use emoji icons and real placeholder data — no broken image URLs.',
        ].join(' ')
      : [
          'You are Aria, an expert website builder. The user is iterating on a single-page',
          'website. Each turn, return ONE complete, standalone HTML document (with inline',
          '<style> — no external assets, no script tags that fetch network resources).',
          'The document must start with <!DOCTYPE html> and end with </html>.',
          'BEFORE the HTML, write one short conversational sentence (under 20 words)',
          'acknowledging the request — e.g. "Here\'s a coffee shop landing page with a',
          'warm tone. Want the hero image swapped?". Keep designs clean, modern, mobile-',
          'friendly. Use real placeholder text and emoji icons (no broken image URLs).',
        ].join(' ');

    const conversationContext = [
      `Current HTML on the page:\n\n${siteHtml}\n\n---\n\nUser's new request: ${text}`,
    ].join('');

    try {
      const res = await fetchWithTimeout(`${API_BASE}/api/ai/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: conversationContext,
          systemPrompt,
          // No persona / voice — text-only.
          surface: 'website-builder',
        }),
        timeout: 90000,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || res.statusText);
      const reply = data?.reply || data?.text || data?.message || '';
      const newHtml = extractHtmlDocument(reply);
      const conversationalLine = stripHtmlFromReply(reply) ||
        (newHtml ? 'Done — refresh me with more changes whenever.' : reply);
      setMessages((prev) => [...prev, { role: 'aria', text: conversationalLine }]);
      if (newHtml) {
        setSiteHtml(newHtml);
        sfx.complete();
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'aria', text: `Hmm, that didn't go through — ${err.message || 'try again'}.`, isError: true },
      ]);
      sfx.error();
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const refreshPreview = () => {
    sfx.click();
    // Force-reload the iframe by briefly clearing srcDoc.
    const current = siteHtml;
    setSiteHtml('');
    setTimeout(() => setSiteHtml(current), 30);
  };

  return (
    <div className="website-builder">
      <h2 className="website-builder__title">Dev Builder</h2>

      {/* Website / App mode switch — individual pill buttons on the LEFT,
          matching the Image / Video / Audio toggle in the AI Generator. */}
      <div className="website-builder__mode-top" role="tablist" aria-label="Builder type">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'website'}
          className={`website-builder__mode-top-btn ${mode === 'website' ? 'active' : ''}`}
          onClick={() => { sfx.toggle(); setMode('website'); }}
        >
          Website
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'app'}
          className={`website-builder__mode-top-btn ${mode === 'app' ? 'active' : ''}`}
          onClick={() => { sfx.toggle(); setMode('app'); }}
        >
          App
        </button>
      </div>

      <div
        className={`website-builder__split ${isDragging ? 'website-builder__split--dragging' : 'website-builder__split--animated'}`}
        ref={splitContainerRef}
        style={{ gridTemplateColumns: `${splitRatio}fr 6px ${1 - splitRatio}fr` }}
      >
        {/* ── LEFT: conversation with Aria ─────────────────────────────── */}
        <section className="website-builder__chat" aria-label="Chat with Aria">
          <header className="website-builder__chat-header">
            <span className="website-builder__chat-emoji" aria-hidden="true">{mode === 'app' ? '📱' : '🌐'}</span>
            <div className="website-builder__chat-headertext">
              <strong>Aria</strong>
              <span className="website-builder__chat-substatus">{loading ? 'building…' : 'ready'}</span>
            </div>
            {/* Chat / Code switch on the right side of the header. Chat shows the
                conversation, Code shows the live HTML source of the preview. */}
            <div className="website-builder__tab-toggle" role="tablist" aria-label="Left pane view">
              <button
                type="button"
                role="tab"
                aria-selected={leftTab === 'chat'}
                className={`website-builder__tab-btn ${leftTab === 'chat' ? 'active' : ''}`}
                onClick={() => { sfx.toggle(); setLeftTab('chat'); }}
              >
                Chat
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={leftTab === 'code'}
                className={`website-builder__tab-btn ${leftTab === 'code' ? 'active' : ''}`}
                onClick={() => { sfx.toggle(); setLeftTab('code'); }}
              >
                Code
              </button>
            </div>
          </header>

          {leftTab === 'chat' ? (
            <div className="website-builder__chat-scroll" ref={chatScrollRef}>
              {messages.map((m, i) => (
                <div key={i} className={`website-builder__msg website-builder__msg--${m.role} ${m.isError ? 'is-error' : ''}`}>
                  <div className="website-builder__msg-bubble">{m.text}</div>
                </div>
              ))}
              {loading && (
                <div className="website-builder__msg website-builder__msg--aria">
                  <div className="website-builder__msg-bubble website-builder__msg-bubble--typing">
                    <span /> <span /> <span />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="website-builder__code-pane">
              <div className="website-builder__code-toolbar">
                <span className="website-builder__code-lang">HTML</span>
                <button
                  type="button"
                  className="website-builder__code-copy"
                  onClick={async () => {
                    sfx.click();
                    try {
                      await navigator.clipboard.writeText(siteHtml);
                      setCodeCopied(true);
                      setTimeout(() => setCodeCopied(false), 1600);
                    } catch (_) {}
                  }}
                >
                  {codeCopied ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
              <pre className="website-builder__code-block"><code>{siteHtml}</code></pre>
            </div>
          )}

          <div className="website-builder__input-row">
            <textarea
              ref={inputRef}
              className="website-builder__input"
              placeholder={mode === 'app' ? 'Describe your app…' : 'Describe your website…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={loading}
            />
            <button
              type="button"
              className="website-builder__send"
              onClick={handleSend}
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              {loading ? '…' : 'Send'}
            </button>
          </div>
        </section>

        {/* ── DIVIDER: drag to resize ─────────────────────────────────── */}
        <div
          className="website-builder__divider"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panels"
          tabIndex={0}
          onMouseDown={onDividerDown}
          onTouchStart={onDividerDown}
          onDoubleClick={() => {
            const mode = previewDevice;
            const def = DEFAULT_RATIOS[mode];
            setSplitRatios(r => ({ ...r, [mode]: def }));
            try { localStorage.removeItem(`wb_split_ratio_${mode}`); } catch(_) {}
          }}
          title="Drag to resize. Double-click to reset."
        >
          <div className="website-builder__divider-grip" aria-hidden="true" />
        </div>

        {/* ── RIGHT: live preview ──────────────────────────────────────── */}
        <section className="website-builder__preview" aria-label="Website preview">
          <header className="website-builder__preview-header">
            <div className="website-builder__device-toggle" role="tablist" aria-label="Preview device">
              <button
                type="button"
                role="tab"
                aria-selected={previewDevice === 'desktop'}
                className={`website-builder__device-btn ${previewDevice === 'desktop' ? 'active' : ''}`}
                onClick={() => { sfx.toggle(); setPreviewDevice('desktop'); }}
              >
                Desktop
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewDevice === 'mobile'}
                className={`website-builder__device-btn ${previewDevice === 'mobile' ? 'active' : ''}`}
                onClick={() => { sfx.toggle(); setPreviewDevice('mobile'); }}
              >
                Mobile
              </button>
            </div>
            <button
              type="button"
              className="website-builder__refresh"
              onClick={resetLayout}
              aria-label="Reset layout"
              title="Reset the panel layout to its default position (does not reload the page)"
            >
              ↻
            </button>
          </header>

          <div className={`website-builder__viewport website-builder__viewport--${previewDevice}`}>
            <div className="website-builder__device-frame">
              <iframe
                title="Website preview"
                className="website-builder__iframe"
                srcDoc={siteHtml}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
